import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import remarkGfm from 'remark-gfm'


import Markdown from 'react-markdown'
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import Badge from '@mui/joy/Badge';
import Tooltip from '@mui/joy/Tooltip';
import IconButton from '@mui/joy/IconButton';
import Divider from '@mui/joy/Divider';
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import useUserIdentity from "hooks/useUserIdentity";
import useLore from 'hooks/useLore';
import { LoreType } from 'stores/lore_store';
import { LoreProvider } from 'contexts/lore_context';
import getIcon from 'utils/getIcon';
import LoadingSection from 'components/LoadingSection';
import { DateOrTime } from './Date';
import { Chip } from '@mui/joy';
import CopyChip from './CopyChip';
const HELP_ICON_NAME = 'AutoStoriesOutlined';


type TT_OPTIONS = 'bottom-end' | 'bottom-start' | 'bottom' | 'left-end' | 'left-start' | 'left' | 'right-end' | 'right-start' | 'right' | 'top-end' | 'top-start' | 'top';

const LoreMarkdown = ({
    doc,
    showInfo=true,
    width = undefined,
    onClose = undefined,
    truncate = false
}:
    {
        doc: LoreType,
        showInfo?: boolean,
        width?: number | undefined,
        onClose?: () => void | undefined,
        truncate?: boolean
    }) => {

    const CloseIcon = getIcon('Close');
        const HelpIcon = getIcon(HELP_ICON_NAME);
        const [overInfo, setOverInfo] = useState(false);
        const [truncated, setTruncated] = useState(truncate);

        // Don't show info on public lore
        showInfo = showInfo && !doc?.slug.startsWith('public');

        const missingDoc = <Stack>
        <Typography>Missing documentation for ${doc?.slug}</Typography>
        <Typography>please mention this to our support team.</Typography>
        </Stack>;
    return (

        <Stack
            // This shouldn't have margins/padding because we dump this into regular content
            spacing={1}
            pb={1}
            direction={onClose ? "column" : "column-reverse"}
            divider={<Divider />}
            maxWidth={width}>
            {showInfo &&
                <Stack
                    width="100%"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1} >
                    <Stack direction="row" spacing={1} alignItems="center"
                        onMouseOver={() => setOverInfo(true)}
                        onMouseOut={() => setOverInfo(false)}
                    >
                        <Link to={`/lore/${doc?.slug}`}>
                            <Typography level="body-xs">
                                <DateOrTime
                                    intro="Last Updated" ts={doc?.date_updated || ''} />
                            </Typography>
                        </Link>

                        { !onClose &&
                            <Stack direction="row"
                                sx={{ opacity: overInfo ? 0.7 : 0,
                                    transition: 'opacity ease-in-out 0.5s'
                                }}>
                                <Typography
                                    endDecorator={<HelpIcon sx={{ opacity: 0.3}} />}
                                    level="body-xs">{doc?.slug}</Typography>
                            </Stack>
                        }
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                        {doc?.slug &&
                            <Tooltip
                                title="Provide feedback about this feature or documentation.">
                                <Link to={`/support/create?feedback=docs&slug=${doc?.slug}`}>
                                    <Typography level="body-xs">
                                        Send Feedback
                                    </Typography>
                                </Link>
                            </Tooltip>
                        }
                        {onClose &&
                            <IconButton
                                onClick={() => onClose()}>
                                <CloseIcon />
                            </IconButton>}

                    </Stack>
                </Stack>
            }

            <Stack >

            { !doc &&
                missingDoc }
                <Markdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        h1(props) {
                            const { children } = props;
                            return <Typography py={2} level="title-lg">{children}</Typography>
                        },
                        h2(props) {
                            const { children } = props;
                            return <Typography py={1} level="title-md">{children}</Typography>
                        },
                        h3(props) {
                            const { children } = props;
                            return <Typography py={1} level="title-sm">{children}</Typography>
                        },
                        table(props) {
                            const { children } = props;
                            return <Table>{children}</Table>
                        },
                        a(props) {
                            const { children, href } = props;
                            var url = href;
                            if(!url) return null;
                            // handle the query string
                            if(url.includes('?')) {
                                const truncated = url.endsWith('?truncated');
                                const included = url.includes('?included');

                                // get the slug without the query string
                                const [s] = url.split('?');
                                return (
                                    <Stack
                                        my={1}
                                        borderBottom={(included || truncated) ? 1 : 0}
                                        borderColor="divider"
                                    >
                                    { !(truncated) &&
                                        <Link to={`/lore/${s}`}>
                                        <Typography
                                            endDecorator={<HelpIcon sx={{ opacity: 0.3}} />}
                                            level="title-lg">
                                            {children}
                                        </Typography>
                                        </Link>
                                    }
                                    { (included || truncated) &&
                                      <LoreContent
                                             truncate={truncated}
                                            showLoading={true}
                                            showInfo={false}
                                          slug={s} />
                                    }
                                    </Stack>);
                            }
                            if (url.endsWith('?tooltip')) {
                                return (
                                    <Stack direction="row" spacing={1}>
                                    <Typography>{children}</Typography>
                                    <LoreDecorator slug={url.replace('?tooltip', '')} />
                                    </Stack>
                                    )

                            }

                            if(!url.startsWith('/') && !url.startsWith('http')) {
                                url = `/lore/${url}`;
                            }
                            if (url.startsWith('/feedback')) {
                                url = `/support/create?feedback=docs&slug=${doc?.slug}`
                            }
                            return <Link to={url}>{children}</Link>
                        },
                        p(props) {
                            const { children } = props;
                            return <Stack py={0.5}><Typography level="body-md">{children}</Typography></Stack>
                        },
                    }}
            >{(truncated ? /* the first 3 lines */ doc?.content.split('\n').slice(0, 3).join('\n') : doc?.content) || 'Docs not loaded'}</Markdown>

            {truncate && <Chip
                             size="sm"
                             variant="plain"
                             onClick={() => setTruncated(!truncated)} >{truncated ? "Read more..." : "Hide"}</Chip>
            }
            </Stack>
            </Stack>
    );
};

const LoreTooltipContent = ({doc, width=undefined, onClose=undefined, slug=''}: {doc: LoreType|null, width?: number|undefined, onClose?: () => void | undefined, slug?: string}) => {
    if (!doc) return <Sheet><Stack p={2} spacing={1}>
        <Typography level="title-lg">Missing documentation</Typography>
        <Typography>Please send this to our support team.</Typography>
        <Typography>Mention this:</Typography>
        <CopyChip copyText={slug}>{slug}</CopyChip>
    </Stack></Sheet>
    return (
        <Stack m={1}
            sx={{ maxHeight: 500, overflow: 'auto' }}
        ><LoreMarkdown onClose={onClose} doc={doc} width={width}/>
        </Stack>);
};

export const LoreTooltip = ({slug, children}:{slug: string, children: React.ReactNode}) => {
    return (
        <LoreProvider>
        <Tooltip arrow variant="outlined" title={<LoreContent slug={slug} />}  sx={{ maxWidth: 500}}>
        <Stack m={1}>
        { children }
        </Stack>
        </Tooltip>
        </LoreProvider>
    );
};

const LoreContent = observer(({
    slug,
    children = null,
    showInfo = true,
    showLoading = false,
    truncate = false }: {
        slug: string,
        children?: React.ReactNode,
        showInfo?: boolean,
    showLoading?: boolean,
    truncate?: boolean
    }) => {

    const { currentIdentity } = useUserIdentity();
    const { fetchBySlug, getBySlug } = useLore();
    const [doc, setDoc] = useState(getBySlug(slug));
    const [fetched, setFetched] = useState(false);

    useEffect(() => {
        if (slug !== doc?.slug) {
            setFetched(false);
        }
    }, [slug]);

        useEffect(() => {
            console.log("lore here");
        if (!fetched && slug) {
            const d = getBySlug(slug);
            if (d) {
                setFetched(true);
                setDoc(d);
            }
            else {
                if(!(slug.startsWith('public') || slug.startsWith('feature')) && !currentIdentity?.uuid) {
                    console.log("Can't fetch (non-public) lore without an identity");
                    return;
                }
                setFetched(true);
                fetchBySlug(currentIdentity.uuid, slug)
                    .then((data) => {
                        setDoc(data);
                    })
                    .catch(() => {
                        console.log(`Unable to load lore: ${slug}`);
                    });
            }
        }
    }, [slug, fetched, currentIdentity?.uuid]);


    if(children) {
        return (
            <Badge
                badgeInset={15}
                variant="plain"
                color="neutral"
                invisible={!doc}
                badgeContent={
                    <LoreDecoratorProvided
                        slug={doc?.slug || ''} />
                }>
                {children}
            </Badge>
        );
    }
    if(doc)
        return <LoreMarkdown truncate={truncate} showInfo={showInfo} doc={doc}  />;
    if(showLoading)
        return <LoadingSection />
    else
        return null;


});



const LoreDecoratorProvided = ({slug, tooltipPlacement=undefined}: {slug: string, tooltipPlacement?: TT_OPTIONS|undefined}) => {

    const [open, setOpen] = useState(false);
    const HelpIcon = getIcon(HELP_ICON_NAME);
    const { currentIdentity } = useUserIdentity();
    const { fetchBySlug, getBySlug } = useLore();
    //    const [doc, setDoc] = useState(getBySlug(slug));
    const doc = getBySlug(slug);
    const [fetched, setFetched] = useState(false);
    // FIXME: lot of duplicate code here with LoreContent
    useEffect(() => {
        if (!fetched && slug) {
            const d = getBySlug(slug);
            if (d) {
                setFetched(true);
                //setDoc(d);
            }
            else {
                if(!(slug.startsWith('public') || slug.startsWith('feature')) && !currentIdentity?.uuid) {
                    console.log("Can't fetch (non-public) lore withou an identity");
                    return;
                }
                fetchBySlug(currentIdentity?.uuid, slug)
                    .then(() => {
                        //setDoc(data);
                        setFetched(true);
                    });
            }
        }
    }, [slug, currentIdentity?.uuid]);


    return (
        <Tooltip
            open={open}
            placement={tooltipPlacement}
            variant={open ? 'soft' : 'solid'}
            title={<LoreTooltipContent
                onClose={() => setOpen(false)}
                       width={400}
                       slug={slug}
                       doc={doc}  />}
            arrow>
            <HelpIcon onClick={() => setOpen(!open)} className="clickable-discrete" />
        </Tooltip>
    );
};

export const LoreDecorator = ({slug, tooltipPlacement=undefined}:{slug: string|undefined, tooltipPlacement?: TT_OPTIONS | undefined}) => {
    if(!slug) return null;
    return (
        <LoreProvider>
            <LoreDecoratorProvided tooltipPlacement={tooltipPlacement} slug={slug} />
        </LoreProvider>
    );

};

const Lore = ({slug, children=null, showInfo=true}:{slug: string, children?: React.ReactNode, showInfo?: boolean}) => {
    return (
        <LoreProvider>
            <LoreContent showInfo={showInfo} slug={slug}>{children}</LoreContent>
        </LoreProvider>
    );

};
export default Lore;
